import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks';
import { organizationLimitsApi, creditPoolApi } from '../../services/limitsApi';
import { OrganizationLimitsFormData, TopupFormData } from '../../types/limits';
import LoadingSpinner from '../common/LoadingSpinner';
import Toast from '../common/Toast';
import {
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  BellIcon,
  PlusIcon,
  PencilIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

interface Organization {
  org_id: number;
  org_name: string;
  monthly_credit_usage?: number;
  user_count?: number;
}

interface OrganizationLimits extends OrganizationLimitsFormData {
  org_id: number;
  created_at: string;
  updated_at: string;
}

const OrganizationLimitsManager: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgLimits, setOrgLimits] = useState<OrganizationLimits | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [formData, setFormData] = useState<OrganizationLimitsFormData>({
    credit_soft_limit: 10000,
    credit_hard_limit: 12000,
    credit_hard_limit_enabled: true,
    credit_pool_monthly: 15000,
    credit_pool_yearly: undefined,
    credit_pool_type: 'monthly',
    security_access: true,
    devops_access: true,
    ide_access: true,
    web_access: true,
    auto_topup_enabled: false,
    auto_topup_amount: 5000,
    auto_topup_threshold: 90,
    notification_threshold_80: true,
    notification_threshold_90: true,
    notification_threshold_95: true,
  });

  const [topupData, setTopupData] = useState<TopupFormData>({
    amount: 1000,
    reason: '',
  });

  // Fetch real organizations from API
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        // Use your existing organizations API endpoint
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1'}/organizations`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const responseData = await response.json();
          
          // The API returns paginated data with { data: [...], meta: {...} }
          const orgsData = responseData.data || responseData;
          
          // Transform the data to match our interface
          const transformedOrgs: Organization[] = orgsData.map((org: any) => ({
            org_id: org.org_id,
            org_name: org.name, // Note: API uses 'name' not 'org_name'
            monthly_credit_usage: org.monthly_credit_usage || 0,
            user_count: org.max_users || 0 // Use max_users as user_count fallback
          }));
          
          setOrganizations(transformedOrgs);
        } else {
          console.error('Failed to fetch organizations:', response.status);
          setToast({ message: 'Failed to load organizations', type: 'error' });
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setToast({ message: 'Failed to load organizations', type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  const loadOrgLimits = async (orgId: number) => {
    try {
      setLoading(true);
      const limits = await organizationLimitsApi.getOrgLimits(orgId);
      setOrgLimits(limits);
      setFormData(limits);
    } catch (error) {
      console.error('Failed to load organization limits:', error);
      setToast({ message: 'Failed to load organization limits', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOrgSelect = (org: Organization) => {
    setSelectedOrg(org);
    setShowForm(false);
    loadOrgLimits(org.org_id);
  };

  const handleSaveLimits = async () => {
    if (!selectedOrg) return;

    try {
      setSaving(true);
      if (orgLimits) {
        await organizationLimitsApi.updateOrgLimits(selectedOrg.org_id, formData);
      } else {
        await organizationLimitsApi.createOrgLimits(selectedOrg.org_id, formData);
      }
      
      setToast({ message: 'Organization limits saved successfully', type: 'success' });
      setShowForm(false);
      loadOrgLimits(selectedOrg.org_id);
    } catch (error) {
      console.error('Failed to save organization limits:', error);
      setToast({ message: 'Failed to save organization limits', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTopup = async () => {
    if (!selectedOrg || !topupData.reason.trim()) return;

    try {
      setSaving(true);
      await creditPoolApi.createTopup(selectedOrg.org_id, {
        ...topupData,
        topup_type: 'manual',
      });
      
      setToast({ message: 'Credit pool top-up completed successfully', type: 'success' });
      setShowTopupModal(false);
      setTopupData({ amount: 1000, reason: '' });
      loadOrgLimits(selectedOrg.org_id);
    } catch (error) {
      console.error('Failed to process top-up:', error);
      setToast({ message: 'Failed to process credit pool top-up', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const renderOrganizationList = () => (
    <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Select Organization
      </h3>
      <div className="space-y-3">
        {organizations.map((org) => (
          <button
            key={org.org_id}
            onClick={() => handleOrgSelect(org)}
            className={`w-full p-4 text-left rounded-lg border transition-colors ${
              selectedOrg?.org_id === org.org_id
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {org.org_name}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {org.user_count} users • {org.monthly_credit_usage?.toLocaleString()} credits used
                </p>
              </div>
              <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderLimitsView = () => {
    if (!selectedOrg) return null;

    if (loading) {
      return (
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        </div>
      );
    }

    if (!orgLimits && !showForm) {
      return (
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="text-center py-8">
            <ShieldCheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Limits Configured
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              This organization doesn't have any limits configured yet.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Limits
            </button>
          </div>
        </div>
      );
    }

    if (showForm) {
      return renderLimitsForm();
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {selectedOrg.org_name} - Limits Configuration
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: {new Date(orgLimits!.updated_at).toLocaleString()}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowTopupModal(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <CurrencyDollarIcon className="w-4 h-4 mr-2" />
                Top-up Credits
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit Limits
              </button>
            </div>
          </div>
        </div>

        {/* Credit Limits */}
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
            Credit Limits
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">Soft Limit</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {orgLimits!.credit_soft_limit.toLocaleString()}
              </div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">Hard Limit</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {orgLimits!.credit_hard_limit.toLocaleString()}
                {orgLimits!.credit_hard_limit_enabled && (
                  <span className="ml-2 text-xs text-green-600 dark:text-green-400">Enabled</span>
                )}
              </div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">Credit Pool</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {(orgLimits!.credit_pool_monthly || orgLimits!.credit_pool_yearly || 0).toLocaleString()}
                <span className="ml-1 text-xs text-gray-500">
                  /{orgLimits!.credit_pool_type}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Access */}
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
            Feature Access
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: 'security_access', label: 'Security' },
              { key: 'devops_access', label: 'DevOps' },
              { key: 'ide_access', label: 'IDE' },
              { key: 'web_access', label: 'Web' },
            ].map((feature) => (
              <div key={feature.key} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {feature.label}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  orgLimits![feature.key as keyof OrganizationLimits]
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                }`}>
                  {orgLimits![feature.key as keyof OrganizationLimits] ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Auto Top-up & Notifications */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
              Auto Top-up
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">Status</span>
                <span className={`text-sm font-medium ${
                  orgLimits!.auto_topup_enabled
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {orgLimits!.auto_topup_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              {orgLimits!.auto_topup_enabled && (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Amount</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {orgLimits!.auto_topup_amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">Threshold</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {orgLimits!.auto_topup_threshold}%
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
            <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
              Notification Thresholds
            </h4>
            <div className="space-y-2">
              {[
                { key: 'notification_threshold_80', label: '80%' },
                { key: 'notification_threshold_90', label: '90%' },
                { key: 'notification_threshold_95', label: '95%' },
              ].map((threshold) => (
                <div key={threshold.key} className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {threshold.label} threshold
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    orgLimits![threshold.key as keyof OrganizationLimits]
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                  }`}>
                    {orgLimits![threshold.key as keyof OrganizationLimits] ? 'On' : 'Off'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLimitsForm = () => (
    <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {orgLimits ? 'Edit' : 'Create'} Organization Limits
        </h3>
        <button
          onClick={() => setShowForm(false)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          ✕
        </button>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); handleSaveLimits(); }} className="space-y-6">
        {/* Credit Limits Section */}
        <div>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
            Credit Limits
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Soft Limit
              </label>
              <input
                type="number"
                value={formData.credit_soft_limit}
                onChange={(e) => setFormData({ ...formData, credit_soft_limit: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Hard Limit
              </label>
              <input
                type="number"
                value={formData.credit_hard_limit}
                onChange={(e) => setFormData({ ...formData, credit_hard_limit: Number(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.credit_hard_limit_enabled}
                onChange={(e) => setFormData({ ...formData, credit_hard_limit_enabled: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable hard limit enforcement
              </span>
            </label>
          </div>
        </div>

        {/* Credit Pool Section */}
        <div>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
            Credit Pool
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pool Type
              </label>
              <select
                value={formData.credit_pool_type}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  credit_pool_type: e.target.value as 'monthly' | 'yearly'
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
              >
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pool Amount
              </label>
              <input
                type="number"
                value={formData.credit_pool_type === 'monthly' ? formData.credit_pool_monthly || 0 : formData.credit_pool_yearly || 0}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  [formData.credit_pool_type === 'monthly' ? 'credit_pool_monthly' : 'credit_pool_yearly']: Number(e.target.value)
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* Feature Access Section */}
        <div>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
            Feature Access
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: 'security_access', label: 'Security' },
              { key: 'devops_access', label: 'DevOps' },
              { key: 'ide_access', label: 'IDE' },
              { key: 'web_access', label: 'Web' },
            ].map((feature) => (
              <label key={feature.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData[feature.key as keyof OrganizationLimitsFormData] as boolean}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    [feature.key]: e.target.checked 
                  })}
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {feature.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Auto Top-up Section */}
        <div>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
            Auto Top-up
          </h4>
          <div className="space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.auto_topup_enabled}
                onChange={(e) => setFormData({ ...formData, auto_topup_enabled: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Enable automatic top-up
              </span>
            </label>
            
            {formData.auto_topup_enabled && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Top-up Amount
                  </label>
                  <input
                    type="number"
                    value={formData.auto_topup_amount}
                    onChange={(e) => setFormData({ ...formData, auto_topup_amount: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Trigger Threshold (%)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={formData.auto_topup_threshold}
                    onChange={(e) => setFormData({ ...formData, auto_topup_threshold: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notification Thresholds Section */}
        <div>
          <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">
            Notification Thresholds
          </h4>
          <div className="space-y-2">
            {[
              { key: 'notification_threshold_80', label: 'Notify at 80% usage' },
              { key: 'notification_threshold_90', label: 'Notify at 90% usage' },
              { key: 'notification_threshold_95', label: 'Notify at 95% usage' },
            ].map((threshold) => (
              <label key={threshold.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData[threshold.key as keyof OrganizationLimitsFormData] as boolean}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    [threshold.key]: e.target.checked 
                  })}
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {threshold.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Limits'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderTopupModal = () => {
    if (!showTopupModal) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-dark-surface">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Credit Pool Top-up
              </h3>
              <button
                onClick={() => setShowTopupModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleTopup(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Top-up Amount
                </label>
                <input
                  type="number"
                  min="1"
                  value={topupData.amount}
                  onChange={(e) => setTopupData({ ...topupData, amount: Number(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Top-up
                </label>
                <textarea
                  value={topupData.reason}
                  onChange={(e) => setTopupData({ ...topupData, reason: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                  placeholder="Enter reason for this top-up..."
                  required
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTopupModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !topupData.reason.trim()}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {saving ? 'Processing...' : 'Add Credits'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Organization List */}
        <div className="lg:col-span-1">
          {renderOrganizationList()}
        </div>

        {/* Limits Configuration */}
        <div className="lg:col-span-2">
          {renderLimitsView()}
        </div>
      </div>

      {/* Top-up Modal */}
      {renderTopupModal()}
    </div>
  );
};

export default OrganizationLimitsManager;
